import DataTableTeamKnowledgeFiles from "@/components/data-table-team-knowledge-files/data-table-team-knowledge-files";
import DialogTeamKnowledgeFileUpload from "@/components/team/dialog-team-knowledge-file-upload";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { TeamKnowledgeFileDTO } from "@shaarp/common";
import { Loader2Icon, PlusIcon } from "lucide-react";
import { useEffect, useState } from "react";

export default function CardTeamKnowledgeFiles() {
  const toastError = useToastError();
  const { t } = useTranslation();

  const api = useApi();

  const [selectedFile, setSelectedFile] = useState<TeamKnowledgeFileDTO | null>(
    null
  );
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [files, setFiles] = useState<TeamKnowledgeFileDTO[]>([]);
  const [showUploadDialog, setShowUploadDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const { data } = await api.get<TeamKnowledgeFileDTO[]>(
        "/my/team/knowledge-base/files"
      );
      setFiles(data);
    } catch (error) {
      setFiles([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const FILES_UPLOAD_SIZE_LIMIT = 50 * 1024 * 1024;
  const FILES_UPLOAD_SIZE_LIMIT_MESSAGE = "50 Mo";

  const onRemoveFile = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.delete<TeamKnowledgeFileDTO[]>(
        `/my/team/knowledge-base/files/${selectedFile!.id}`
      );
      setFiles(data);
      toast({
        variant: "default",
        title: t("team.knowledge.deleteSuccess.title"),
        description: t("team.knowledge.deleteSuccess.description", {
          name: selectedFile!.name,
        }),
      });
    } catch (error) {
      toastError();
    } finally {
      setSelectedFile(null);
      setIsLoading(false);
    }
  };

  const processFilesUpload = async (files: File[]) => {
    if (files.some((file) => file.size > FILES_UPLOAD_SIZE_LIMIT)) {
      toast({
        variant: "warning",
        title: t("chat.input.files.tooLarge.title"),
        description: t("chat.input.files.tooLarge.description", {
          size: FILES_UPLOAD_SIZE_LIMIT_MESSAGE,
        }),
      });
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });
      const { data } = await api.post<TeamKnowledgeFileDTO[]>(
        "/my/team/knowledge-base/files",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          maxContentLength: FILES_UPLOAD_SIZE_LIMIT,
          maxBodyLength: FILES_UPLOAD_SIZE_LIMIT,
        }
      );
      setFiles(data);
      toast({
        variant: "default",
        title: t("team.knowledge.uploadSuccess.title", {
          count: files.length,
        }),
        description: t("team.knowledge.uploadSuccess.description", {
          count: files.length,
        }),
      });
    } catch (error) {
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Card className="w-full">
        <CardHeader>
          <div className="flex justify-between">
            <div>
              <CardTitle>{t("team.knowledge.documents.title")}</CardTitle>
              <CardDescription>
                {t("team.knowledge.documents.description")}
              </CardDescription>
            </div>
            <div>
              <Button
                onClick={() => setShowUploadDialog(true)}
                disabled={isLoading || isUploading}
              >
                {isUploading ? (
                  <Loader2Icon className="mr-2 animate-spin size-4" />
                ) : (
                  <PlusIcon className="mr-2 size-4" />
                )}
                {t("team.knowledge.addFile")}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTableTeamKnowledgeFiles
            files={files}
            isLoading={isLoading}
            onDelete={(file) => setSelectedFile(file)}
          />
        </CardContent>
      </Card>

      <DialogTeamKnowledgeFileUpload
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        onUpload={processFilesUpload}
        isUploading={isUploading}
      />

      <AlertDialog open={selectedFile !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("team.dialog.knowledge.delete.title", {
                name: selectedFile?.name,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("team.dialog.knowledge.delete.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setSelectedFile(null)}
              disabled={isLoading}
            >
              {t("listing.dialog.update.delete.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onRemoveFile();
              }}
              disabled={isLoading}
            >
              {isLoading && (
                <Loader2Icon className="mr-2 animate-spin size-4" />
              )}
              {t("listing.dialog.update.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
